import type { Icon } from '@phosphor-icons/react';
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cn } from './cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft';
type Size = 'sm' | 'md' | 'lg';

const base =
  'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-btn)] font-semibold transition-[background-color,color,box-shadow,transform] duration-150 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100';

const variants: Record<Variant, string> = {
  primary: 'bg-ink text-sheet hover:bg-ink/90',
  secondary: 'bg-sheet text-ink ring-1 ring-line ring-inset hover:bg-sheet-2',
  ghost: 'bg-transparent text-ink hover:bg-ink/6',
  danger: 'bg-alert text-white hover:bg-alert/90',
  soft: 'bg-action-soft text-action hover:bg-action-soft/80',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-[0.9375rem]',
  lg: 'h-13 px-5 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: Icon;
  loading?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon: IconCmp,
    loading,
    block,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <Spinner />
      ) : IconCmp ? (
        <IconCmp size={size === 'sm' ? 16 : 19} weight="bold" aria-hidden />
      ) : null}
      {children}
    </button>
  );
});

export interface ButtonLinkProps extends LinkProps {
  variant?: Variant;
  size?: Size;
  icon?: Icon;
  block?: boolean;
}

export function ButtonLink({
  variant = 'primary',
  size = 'md',
  icon: IconCmp,
  block,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...rest}
    >
      {IconCmp ? <IconCmp size={size === 'sm' ? 16 : 19} weight="bold" aria-hidden /> : null}
      {children}
    </Link>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: Icon;
  label: string;
  variant?: 'ghost' | 'secondary' | 'primary';
  size?: 'sm' | 'md';
  badge?: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: IconCmp, label, variant = 'ghost', size = 'md', badge, className, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full transition-colors duration-150 active:scale-95',
        size === 'md' ? 'h-11 w-11' : 'h-9 w-9',
        variant === 'ghost' && 'text-ink hover:bg-ink/6',
        variant === 'secondary' && 'bg-sheet text-ink ring-1 ring-line ring-inset hover:bg-sheet-2',
        variant === 'primary' && 'bg-ink text-sheet hover:bg-ink/90',
        className,
      )}
      {...rest}
    >
      <IconCmp size={size === 'md' ? 22 : 18} weight="regular" aria-hidden />
      {badge}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Caricamento"
      className={cn(
        'inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent',
        className,
      )}
    />
  );
}
